import { emailColors } from '@/utils/constants';
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Collapsed by default; see useDashboardSidebar.
  dashboardSidebarExpanded: false,
  showCompetitionDetailsOnTopbar: false,
  showBackButtonOnTopbar: false,
  currentContextData: null,
  currentNavigationLevel: 1,
  showPlanExpired: false,
  showOnboardingDialog: false,
  onBoardingStep: 0,
  onBoardingCompletionPercentage: {
    0: 0,
    1: 0,
    2: 0,
    3: 0,
  },
  showFeatureRestricted: false,
  questionFrameworkSteps: [],
  activeQuestionFrameworkStep: 0,
  resetQuestionFrameworkStep: false,
  creditsRemaining: 0,
  loggedInUserTeamDetails: null,
  teamId: null,
  pinnedTeams: [],
  currentTeamToManage: null,
  showCreditsRequired: false,
  // Sidebar nav groups the user has collapsed, keyed by trimmed group title.
  // Stored as *collapsed* rather than expanded so that a missing key means
  // expanded: groups are open by default, and a group added later opens by
  // default too, without anything having to seed this map.
  navCollapsedGroups: {},
  // Preferences > "Show two level navigation bar": the Slack-style icon rail
  // of nav groups beside the full nav. Off gives the one-level sidebar.
  // Mirrors `userSetting.twoLevelNavigation` (see `useNavPreferencesSync`);
  // persisted (see `uiPersistConfig`) so reloads don't flash, on by default.
  twoLevelNav: true,
  // Preferences > "Keep the navigation bar collapsed": from md up the sidebar
  // stays collapsed to its rail and the expand/collapse toggle is hidden. Off
  // starts it expanded, with the toggle. Mirrors
  // `userSetting.sidebarCollapsedByDefault`; persisted, and on by default.
  navCollapsedByDefault: true,
  // Whether the user last left the desktop sidebar expanded with its toggle.
  // Only read while `navCollapsedByDefault` is off, so the sidebar reopens
  // the way it was left; `dashboardSidebarExpanded` itself also tracks the
  // mobile drawer, so it is not the one persisted. Persisted, and expanded
  // by default.
  navSidebarExpanded: true,
  // Preferences > "Live transcription while recording": the meeting recorder
  // (/m-record) streams mic and system audio to the realtime transcription
  // API only when this is on. Off just records the streams for download.
  // Persisted, and off by default.
  liveTranscriptionEnabled: false,
  // Tri-state, and deliberately not `false`: `null` means "the org config has
  // not resolved yet, so we do not know whether onboarding is done". It starts
  // unknown because `uiStates` persists only `navCollapsedGroups`, so a `true`
  // default opened a fullscreen wizard-spinner over an already-painted app on
  // every single load for every fully-onboarded user. `useOnboardingProgress`
  // resolves it to `true`/`false` once `/us/org/configs` lands; consumers that
  // read it as "is onboarding up" must test `=== true` / `=== false` rather
  // than truthiness, so the unknown window is not mistaken for a settled "no".
  showOnboardingDialogV2: null,
  crmOrTool: 'crm',
  crmSearch: '',
  toolSearch: '',
  onboardingStepV2: '',
  completedStepsV2: [],
  onboardingStage: '',
  asyncRequestId: null,
  accountResearchAsyncReqId: null,
  asyncLookup: null,
  asyncStakeholderUpdate: [],
  asyncGmailSync: null,
  newStakeholderOrSellerDetected: null,
  refreshField: null,
  leftnavMode: 'new',
  tableWithIcons: false,
  unassociatedItemsCount: 0,
  mailboxUnrepliedCount: 0,
  iconsLibrary: 'lucide',
  showReintegrateCalendarDialog: false,
  // Global dialog open flags moved out of `Dashboard.js` local state so the
  // dialog components can select their own flag and dispatch their own close.
  showOnboardingCompleted: false,
  // Holds the searched-for CRM/tool name; '' means closed.
  showCrmOrToolDoesntExist: '',
  // Holds the feature name ('gmail' | 'outlook' | 'google'); false means closed.
  showForcedTurnedOffFeature: false,
  showNewRiskDialog: false,
  showDisclosure: false,

  // ---- Shared integration dialogs (Phase 3) --------------------------------
  // These dialogs are rendered exactly once, from `GlobalDialogs` in the
  // Dashboard layout, and are opened from both the onboarding flow in
  // `Dashboard.js` and the `/integrations` page. Their open flags therefore
  // live here rather than in either component's local state.
  //
  // `showHubspotKeyDialog` carries the opening context: `null` means closed,
  // `{ readOnly: bool }` means open with that CRM-access choice, snapshotted
  // from whichever page's Read/Read-Write radio opened it.
  showHubspotKeyDialog: null,
  // Same opening-context shape as `showHubspotKeyDialog`.
  showZohoKeyDialog: null,
  showFathomKeyDialog: false,
  showAircallIntegrationDialog: false,
  // '' | 'new' | 'update'
  showSlackIntegrationDialog: '',
  // Post-connect configuration for the OAuth Slack integration
  // (`SLACK_OAUTH`) - channels per notification type plus user tagging.
  // Opened from the `/integrations` Slack card, from the onboarding wizard's
  // tools step, and by the `/oauth/slack` landing page on the way back in.
  showSlackOAuthConfigDialog: false,
  showZoomIntegrationDialog: false,
  // Bumped by a shared integration dialog after a successful connect so that
  // every mounted page re-runs its own `refreshIntegrations()`. Replaces the
  // direct `refreshIntegrations()` call the inline copies used to make.
  integrationsRefreshSignal: 0,
  // Handed to the `/integrations` page after a connect so it can open its CRM
  // user-mapping dialog. Consumed and cleared there.
  pendingCrmUserMapping: null,
  persistedDataFor5Sec: {},
  showCreateNewDealDialog: null,
  createNewDealFor: null,
  dealCreationMode: 'normal',
  refreshDealList: false,
  selectedCrmDealState: null,
  asyncDealMeetingPrep: null,
  selectedDealsLabel: 'All Deals',
  selectedDealsMemberId: null,
  selectedConvUser: null,
  experimentalFeatureEnabled: false,
  asyncThreadProcessing: null,
  asyncMeetingPrepGoals: null,
  showCreateMeetingItemDialog: null,
  asyncMeetingActionItem: null,
  showMeetingObjectives: null,
  asyncDealHighlights: null,
  asyncCompanyResearch: null,
  showUndoSendTimer: null,
  showGmailIntegrationDialog: null,
  invitationDetails: null,
  replyModeThread: null,
  // new states
  dealPage: {
    viewType: 'activities',
    activeArtifact: null,
    activeArtifactTab: 'summary',
    currentArtifactDetailTab: 'summary',
    threadedView: true,
    showSyncCrmPropertiesDialog: null,
    maximizedTimelineView: false,
    showUpdateSpeakerModal: null,
    refreshOrgChart: false,
    showUpdateDealCompanyInfo: null,
    savingDealCompanyInfo: false,
    extractingCompanyInfo: false,
    companyResearchDataEditMode: null,
    maximizedOrgChart: false,
    orgChartDirty: false,
    pendingUnsavedAction: null,
    currentAccountPlanTab: 'stakeholders',
    activeStakeholder: null,
    activeSeller: null,
    showAddStakeholdersDialog: null,
    showDesignationMissingWarning: false,
    handleStakeholderResearchCallback: false,
    triggerStakeholderResearch: false,
    showConfirmDeleteStakeholder: null,
    showAddNewSellerDialog: null,
    showConfirmDeleteSeller: null,
    triggerNBAGeneration: false,
  },
  // Last report opened from the Reports page, highlighted on the way back.
  lastOpenedReportId: null,
  emailColors: emailColors,
  showEmailColorConfigurator: false,
  showUploadArtifact: false,
  asyncExpertBotConversation: null,
  askExpertResetKey: 0,
  showLoginToast: false,
  onboardingSettings: {
    onBoarding: false,
    onBoardingStep: null,
    onBoardingFor: null,
    stopAt: null,
  },
};

const uiSlice = createSlice({
  name: 'uiStates',
  initialState,
  reducers: {
    updateUiState: (state, action) => {
      const { key, value } = action.payload;
      // `dealPage` is updated frequently from async callbacks; merge partial
      // patches to avoid stale snapshot overwrites.
      if (
        key === 'dealPage' &&
        value &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        // Assign only the fields that actually differ. `Object.assign` always
        // touched the immer draft, so every patch produced a new `dealPage`
        // identity even when no value changed - and with 66 components
        // subscribed to the whole `uiStates` slice, that re-rendered the app
        // and re-fired any effect holding `dealPage` (or a field of it) in its
        // dependency array. Writing nothing when nothing changed keeps the
        // previous identity and breaks that loop.
        Object.keys(value).forEach((field) => {
          if (!Object.is(state.dealPage[field], value[field])) {
            state.dealPage[field] = value[field];
          }
        });
        return;
      }
      // Same reasoning for the flat keys: a no-op write still invalidates every
      // subscriber of `uiStates`.
      if (!Object.is(state[key], value)) {
        state[key] = value;
      }
    },
    // Ask every mounted page to re-run its own `refreshIntegrations()`.
    bumpIntegrationsRefreshSignal: (state) => {
      state.integrationsRefreshSignal += 1;
    },
    updateOnboardingStepCompletionPercentage: (state, action) => {
      const { key, value } = action.payload;
      if (state.onBoardingCompletionPercentage) {
        state.onBoardingCompletionPercentage[key] = value;
      }
    },
    addAsyncStakeholderUpdate: (state, action) => {
      const entry = action.payload;
      const list = Array.isArray(state.asyncStakeholderUpdate)
        ? state.asyncStakeholderUpdate
        : [];
      state.asyncStakeholderUpdate = [
        ...list.filter((u) => u.requestId !== entry.requestId),
        entry,
      ];
    },
    removeAsyncStakeholderUpdate: (state, action) => {
      const requestId = action.payload;
      state.asyncStakeholderUpdate = (
        state.asyncStakeholderUpdate || []
      ).filter((u) => u.requestId !== requestId);
    },
    resetUiState: () => initialState,
  },
});

export const {
  updateUiState,
  bumpIntegrationsRefreshSignal,
  updateOnboardingStepCompletionPercentage,
  addAsyncStakeholderUpdate,
  removeAsyncStakeholderUpdate,
  resetUiState,
} = uiSlice.actions;

export default uiSlice.reducer;
